// App-wide confirm dialog — replaces native window.confirm(), whose look is
// uncontrollable in the Android WebView (wrong colors in dark mode, inconsistent
// copy, hard for e2e to drive). See docs/audits 2026-07-01 P2-4.
//
// Module-level singleton (mirrors src/lib/toast.ts) so any call site can do a
// one-line swap from `if (confirm(msg)) { … }` to
// `if (await confirmDialog({ title: msg })) { … }` — no hook, no context, no
// per-component open-state plumbing. Render <ConfirmHost /> once at the root.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppDrawer } from '@/components/ui/AppDrawer';

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for delete / irreversible actions. */
  destructive?: boolean;
}

type Opener = (opts: ConfirmOptions) => Promise<boolean>;
let opener: Opener | null = null;

/** Show the confirm sheet; resolves true if the user confirms. */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  // <ConfirmHost /> not mounted yet (shouldn't happen in-app) — fail safe to
  // the native dialog rather than silently swallowing the action.
  if (!opener) return Promise.resolve(window.confirm(opts.title));
  return opener(opts);
}

export function ConfirmHost() {
  const { t } = useTranslation();
  const [state, setState] = useState<{
    opts: ConfirmOptions;
    resolve: (v: boolean) => void;
  } | null>(null);

  useEffect(() => {
    opener = (opts) => new Promise<boolean>((resolve) => setState({ opts, resolve }));
    return () => {
      opener = null;
    };
  }, []);

  const close = (result: boolean) => {
    setState((s) => {
      s?.resolve(result);
      return null;
    });
  };

  const opts = state?.opts;

  return (
    <AppDrawer
      open={!!state}
      onOpenChange={(o) => {
        if (!o) close(false);
      }}
      title={opts?.title ?? ''}
      description={opts?.description}
    >
      <div className="flex flex-col gap-2 pb-2">
        <button
          type="button"
          onClick={() => close(true)}
          className={`inline-flex min-h-[44px] w-full items-center justify-center rounded-md px-3 text-sm font-medium ${
            opts?.destructive
              ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              : 'bg-primary text-primary-foreground hover:opacity-90'
          }`}
        >
          {opts?.confirmLabel ?? t('common.confirm')}
        </button>
        <button
          type="button"
          onClick={() => close(false)}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-md border bg-background px-3 text-sm font-medium hover:bg-muted"
        >
          {opts?.cancelLabel ?? t('common.cancel')}
        </button>
      </div>
    </AppDrawer>
  );
}
